import React from 'react';
import clsx from 'clsx';

type BadgeVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'gray';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  className?: string;
  dot?: boolean;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-fill-2 text-zinc-300',
  primary: 'bg-accent-bg text-accent border border-accent-border',
  success: 'bg-success-bg text-success border border-success-border',
  warning: 'bg-accent-bg text-accent border border-accent-border',
  danger: 'bg-danger-bg text-danger border border-danger-border',
  info: 'bg-fill-2 text-zinc-400',
  gray: 'bg-fill-2 text-zinc-500',
};

const dotVariantClasses: Record<BadgeVariant, string> = {
  default: 'bg-zinc-400',
  primary: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-accent',
  danger: 'bg-danger',
  info: 'bg-zinc-500',
  gray: 'bg-zinc-600',
};

const Badge: React.FC<BadgeProps> = ({ children, variant = 'default', className, dot = false }) => {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-[11px] md:text-xs font-medium',
        variantClasses[variant],
        className,
      )}
    >
      {dot && (
        <span className={clsx('h-1.5 w-1.5 rounded-full', dotVariantClasses[variant])} />
      )}
      {children}
    </span>
  );
};

export default Badge;

// Helper functions for ticket status and priority badges
export const getStatusBadgeVariant = (status: string): BadgeVariant => {
  const map: Record<string, BadgeVariant> = {
    new: 'primary',
    open: 'info',
    in_progress: 'warning',
    waiting_client: 'warning',
    waiting_response: 'warning',
    waiting_specialist: 'primary',
    resolved: 'success',
    closed: 'gray',
  };
  return map[status] || 'default';
};

export const getStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    new: 'Новый',
    open: 'Открыт',
    in_progress: 'В работе',
    waiting_client: 'Ожидает ответа',
    waiting_response: 'Ожидает ответа',
    waiting_specialist: 'У специалиста',
    resolved: 'Решён',
    closed: 'Закрыт',
  };
  return map[status] || status;
};

export const getPriorityBadgeVariant = (priority: string): BadgeVariant => {
  const map: Record<string, BadgeVariant> = {
    low: 'gray',
    normal: 'default',
    high: 'warning',
    critical: 'danger',
    urgent: 'danger',
  };
  return map[priority] || 'default';
};

export const getPriorityLabel = (priority: string): string => {
  const map: Record<string, string> = {
    low: 'Низкая',
    normal: 'Обычная',
    high: 'Высокая',
    critical: 'Критическая',
    urgent: 'Срочная',
  };
  return map[priority] || priority;
};
